import moment from 'moment-timezone'
import useAppDispatch from '../../../hooks/useAppDispatch'
import useEventTimezone from '../../../hooks/useEventTimezone'
import { getDirtyBibSelector, getDirtyTagSelector, getSelectedEntrySelector, getSelectedEntryTeamSelector, PanelState, setDirtyInput, setEnterKeyPress, setPanelState, setSelectedEntry } from '../checkinSlice'
import useAppSelector from '../../../hooks/useAppSelector'
import removeLeadingZeroes from '../../../utils/removeLeadingZeroes'
import { useCallback, useMemo } from 'react'
import { isRelaySharedBibTeam } from '../../../components/common/Bib'
import { addUnsyncedRequests } from '../../data/dataSlice'
import useCheckinSettings from './useCheckinSettings'
import { setSnackbarMessages } from '../../snackbar/snackbarSlice'

const useUnCheckInEntry = () => {
  const dispatch = useAppDispatch()
  const eventTimezone = useEventTimezone()
  const { checkinAllSettings } = useCheckinSettings()
  const askForTag = checkinAllSettings.settings?.askForTag
  const selectedEntry = useAppSelector(getSelectedEntrySelector)
  const dirtyBib = removeLeadingZeroes(useAppSelector(getDirtyBibSelector) || '')
  const dirtyTag = useAppSelector(getDirtyTagSelector) || ''
  const team = useAppSelector(getSelectedEntryTeamSelector)
  const isRelaySharedBib = team && isRelaySharedBibTeam(team)
  const selectedEntryWithTeam = useMemo(() => {
    return selectedEntry && team ? [selectedEntry, team] : []
  }, [selectedEntry, team])

  return useCallback((removeBib: boolean = false) => {
    // must need an entry selected
    if (!selectedEntry) {
      dispatch(setPanelState(PanelState.ERROR))
      return
    }

    const bib = dirtyBib && !removeBib ? dirtyBib : ''
    const tag = askForTag ? (dirtyTag && !removeBib ? dirtyTag : '') : undefined
    const lastModifiedTime = moment.tz(eventTimezone!).unix()

    // update relay team bibs accordingly
    if (isRelaySharedBib && dirtyBib && selectedEntryWithTeam?.length) {
      selectedEntryWithTeam.forEach(entry => {
        const isCurrentEntrySelected = selectedEntry.entryId === entry.entryId
        dispatch(addUnsyncedRequests([
          {
            regChoiceName: entry.regChoiceName,
            raceId: entry.raceId,
            entryId: entry.entryId,
            teamId: entry.teamId,
            teamType: entry.teamType,
            teamShareBib: entry.teamShareBib,
            lastModifiedTime,
            bib: !isCurrentEntrySelected ? bib : '',
            tag: askForTag ? (!isCurrentEntrySelected ? tag : '') : undefined,
            checkedIn: isCurrentEntrySelected ? false : entry.checkedIn,
            checkInTime: isCurrentEntrySelected ? lastModifiedTime : entry.checkInTime,
            chipDisambiguationPolicy: checkinAllSettings.event.chipDisambiguationPolicy ?? 'NONE'
          }
        ]))
      })
    } else {
      dispatch(addUnsyncedRequests([
        {
          regChoiceName: selectedEntry.regChoiceName,
          raceId: selectedEntry.raceId,
          entryId: selectedEntry.entryId,
          teamId: selectedEntry.teamId,
          teamType: selectedEntry.teamType,
          teamShareBib: selectedEntry.teamShareBib,
          checkedIn: false,
          lastModifiedTime,
          checkInTime: null,
          bib,
          tag,
          chipDisambiguationPolicy: checkinAllSettings.event.chipDisambiguationPolicy ?? 'NONE'
        }
      ]))
    }

    dispatch(setSelectedEntry(undefined))
    dispatch(setPanelState(PanelState.HIDDEN))
    dispatch(setDirtyInput(false))
    dispatch(setEnterKeyPress(false))
    dispatch(setSnackbarMessages({
      messageLines: [
        {
          message: dirtyBib
            ? removeBib
              ? `${selectedEntry.fullName} has been un-checked and their bib number ${dirtyBib} is made available!`
              : `${selectedEntry.fullName} has been un-checked and kept their bib number ${dirtyBib}`
            : `${selectedEntry.fullName} has been un-checked`
        }
      ],
      severity: 'success'
    }))
  }, [
    dirtyBib,
    dirtyTag,
    askForTag,
    eventTimezone,
    dispatch,
    selectedEntry,
    isRelaySharedBib,
    selectedEntryWithTeam,
    checkinAllSettings.event.chipDisambiguationPolicy,
  ])
}

export default useUnCheckInEntry
